import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { useNotifications } from '@/contexts/NotificationContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { formatDateTimeIST } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FolderOpen,
  MessageCircle,
  MessageSquareReply,
  User,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CompletedTasksNotificationProps {
  // eslint-disable-next-line no-unused-vars
  onTaskClick?: (taskId: string) => void;
}

const CREATOR_ROLES = ['admin', 'gm', 'pm', 'hod', 'dpm'];

export default function CompletedTasksNotification({
  onTaskClick,
}: CompletedTasksNotificationProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [replyTaskId, setReplyTaskId] = useState<string | null>(null);
  const [replyComment, setReplyComment] = useState('');
  const [extendDueDate, setExtendDueDate] = useState('');
  const { removeNotificationsForTask } = useNotifications();

  const isCreatorRole = user?.role && CREATOR_ROLES.includes(user.role);
  const wasOpenRef = useRef(false);

  // API: Completed tasks pending acknowledgment (creator has not acknowledged yet)
  // Refetched when any WebSocket notification arrives (useWebSocket invalidates this query).
  const {
    data: pendingTasks = [],
    isLoading: pendingTasksLoading,
    isFetched: pendingTasksFetched,
    refetch: refetchPendingTasks,
  } = useQuery({
    queryKey: ['/api/completed-tasks/pending-acknowledgment'],
    queryFn: async () => {
      const res = await apiRequest(
        'GET',
        '/api/completed-tasks/pending-acknowledgment'
      );
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!(user?.id && isCreatorRole),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  // When the popup is re-opened (and we already fetched before), refetch to get the latest.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    if (!wasOpen && isOpen && pendingTasksFetched) {
      refetchPendingTasks();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, pendingTasksFetched, refetchPendingTasks]);

  const acknowledgeMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await apiRequest('POST', `/api/tasks/${taskId}/acknowledge-completion`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/api/completed-tasks/pending-acknowledgment'],
      });
    },
  });

  const handleAcknowledge = (taskId: string) => {
    acknowledgeMutation.mutate(taskId);
  };

  const handleAcknowledgeAll = () => {
    pendingTasks.forEach((task: { id: string }) => {
      acknowledgeMutation.mutate(task.id);
    });
  };

  // Fetch task for Reply modal (single task by id)
  const { data: replyTask, isLoading: replyTaskLoading } = useQuery({
    queryKey: ['/api/tasks', replyTaskId],
    queryFn: async () => {
      if (!replyTaskId) return null;
      const res = await apiRequest('GET', `/api/tasks/${replyTaskId}`);
      if (!res.ok) throw new Error('Failed to fetch task');
      return res.json();
    },
    enabled: !!replyTaskId,
  });

  // Fetch previous comments for Reply modal
  const { data: replyCommentsData, isLoading: commentsLoading } = useQuery({
    queryKey: [`/api/tasks/${replyTaskId}/comments`],
    queryFn: async () => {
      if (!replyTaskId) return [];
      const res = await apiRequest('GET', `/api/tasks/${replyTaskId}/comments`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data.items || [];
    },
    enabled: !!replyTaskId,
  });
  const replyComments = replyCommentsData || [];

  const reopenMutation = useMutation({
    mutationFn: async () => {
      if (!replyTaskId || !replyComment.trim())
        throw new Error('Comment is required');
      await apiRequest('POST', `/api/tasks/${replyTaskId}/comments`, {
        content: replyComment.trim(),
      });
      const patchBody: { status: string; dueDate?: string } = {
        status: 'reopened',
      };
      if (extendDueDate) {
        patchBody.dueDate = new Date(extendDueDate).toISOString();
      }
      await apiRequest('PATCH', `/api/tasks/${replyTaskId}`, patchBody);
    },
    onSuccess: () => {
      const taskId = replyTaskId;
      toast({
        title: 'Task Reopened',
        description: extendDueDate
          ? 'Your reply was added, due date extended, and the task has been reopened for the assignee.'
          : 'Your reply was added and the task has been reopened for the assignee.',
      });
      setReplyTaskId(null);
      setReplyComment('');
      setExtendDueDate('');
      if (taskId) removeNotificationsForTask(taskId);
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/completed-tasks/pending-acknowledgment'],
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to reopen task',
        variant: 'destructive',
      });
    },
  });

  const handleReplySubmit = () => {
    if (!replyComment.trim()) {
      toast({
        title: 'Comment required',
        description: 'Please add a comment before reopening.',
        variant: 'destructive',
      });
      return;
    }
    reopenMutation.mutate();
  };

  // Show component only for creator roles when there are pending tasks or still loading
  if (!isCreatorRole || (pendingTasks.length === 0 && !pendingTasksLoading)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        // Floating icon button
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full h-14 w-14 shadow-lg hover:shadow-xl transition-all duration-200 bg-green-600 hover:bg-green-700 text-white relative"
          size="lg"
        >
          <CheckCircle2 className="h-6 w-6" />
          {pendingTasks.length > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs animate-pulse"
            >
              {pendingTasks.length > 99 ? '99+' : pendingTasks.length}
            </Badge>
          )}
        </Button>
      ) : (
        // Notification panel
        <div
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 transition-all duration-200 ${
            isMinimized ? 'w-80' : 'w-96'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20 rounded-t-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Completed Tasks
              </h3>
              {pendingTasks.length > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {pendingTasks.length} new
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {pendingTasks.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAcknowledgeAll}
                  className="text-xs h-7"
                >
                  Acknowledge All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="h-7 w-7 p-0"
              >
                {isMinimized ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <>
              <ScrollArea className="h-96">
                {pendingTasksLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading...
                  </div>
                ) : pendingTasks.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No completed tasks pending acknowledgment
                  </div>
                ) : (
                  <div className="p-2">
                    {pendingTasks.map(
                      (task: {
                        id: string;
                        title: string;
                        assignee?: {
                          firstName?: string;
                          lastName?: string;
                          username?: string;
                        };
                        assignees?: Array<{
                          id: string;
                          firstName?: string | null;
                          lastName?: string | null;
                          username?: string | null;
                        }>;
                        project?: { name?: string };
                        team?: { name?: string };
                        completedAt?: string | Date;
                      }) => {
                        const assigneeList = task.assignees;
                        const names = assigneeList?.length
                          ? assigneeList.map(
                              u =>
                                `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() ||
                                u?.username ||
                                'Unassigned'
                            )
                          : [];
                        const assigneeDisplay =
                          names.length > 1
                            ? `Shared with: ${names.join(', ')}`
                            : names.length === 1
                              ? names[0]
                              : task.assignee
                                ? `${task.assignee.firstName || ''} ${task.assignee.lastName || ''}`.trim() ||
                                  task.assignee.username ||
                                  'Unassigned'
                                : 'Unassigned';
                        const teamName =
                          task.team?.name || task.project?.name || 'No Team';
                        return (
                          <div
                            key={task.id}
                            className="p-3 mb-2 rounded-lg border transition-colors bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                    Task Completed
                                  </div>
                                  <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                                </div>
                                <div
                                  className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 rounded-md text-xs font-semibold inline-block max-w-full truncate mt-1 cursor-pointer hover:opacity-80"
                                  title={task.title}
                                  onClick={() => onTaskClick?.(task.id)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={e =>
                                    e.key === 'Enter' && onTaskClick?.(task.id)
                                  }
                                >
                                  {task.title}
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {assigneeDisplay}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <FolderOpen className="h-3 w-3" />
                                    {teamName}
                                  </span>
                                </div>
                                {task.completedAt && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {formatDistanceToNow(
                                      new Date(task.completedAt),
                                      { addSuffix: true }
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAcknowledge(task.id)}
                                className="text-xs h-7 flex-1"
                                disabled={acknowledgeMutation.isPending}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Acknowledge
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setReplyTaskId(task.id);
                                  setReplyComment('');
                                  setExtendDueDate('');
                                }}
                                className="text-xs h-7"
                                title="Reply and reopen task for assignee"
                              >
                                <MessageSquareReply className="h-3 w-3 mr-1" />
                                Reply
                              </Button>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </ScrollArea>
            </>
          )}
        </div>
      )}

      {/* Reply & Reopen Task Dialog */}
      <Dialog
        open={!!replyTaskId}
        onOpenChange={open =>
          !open && (setReplyTaskId(null), setExtendDueDate(''))
        }
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reply & Reopen Task</DialogTitle>
            <DialogDescription>
              Add a comment for the assignee. The task will be reopened and
              appear in their Started list with status &quot;Reopened&quot;. You
              can optionally extend the due date.
            </DialogDescription>
          </DialogHeader>
          {replyTaskLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              Loading task...
            </div>
          ) : replyTask ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-gray-50 dark:bg-gray-900/50 p-3 text-sm">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {replyTask.title}
                </div>
                {replyTask.assignee && (
                  <div className="mt-1 text-gray-600 dark:text-gray-400">
                    Assignee: {replyTask.assignee.firstName}{' '}
                    {replyTask.assignee.lastName}
                  </div>
                )}
                {(replyTask.team?.name || replyTask.project?.name) && (
                  <div className="text-gray-600 dark:text-gray-400">
                    Project: {replyTask.team?.name || replyTask.project?.name}
                  </div>
                )}
              </div>

              {/* Previous Comments */}
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <MessageCircle className="h-4 w-4" />
                  Previous Comments ({replyComments.length})
                </div>
                <ScrollArea className="h-32 rounded-md border bg-gray-50 dark:bg-gray-900/30 p-2">
                  {commentsLoading ? (
                    <div className="py-4 text-center text-xs text-gray-500">
                      Loading comments...
                    </div>
                  ) : replyComments.length === 0 ? (
                    <div className="py-4 text-center text-xs text-gray-500">
                      No comments yet
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {replyComments.map(
                        (c: {
                          id: string;
                          content: string;
                          userId: string;
                          createdAt: string;
                          user?: {
                            firstName?: string;
                            lastName?: string;
                            username?: string;
                          };
                        }) => (
                          <div
                            key={c.id}
                            className="text-xs border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0"
                          >
                            <div className="font-medium text-gray-700 dark:text-gray-300">
                              {c.user
                                ? `${c.user.firstName || ''} ${c.user.lastName || ''}`.trim() ||
                                  c.user.username
                                : 'Unknown'}
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mt-0.5 whitespace-pre-wrap">
                              {c.content}
                            </p>
                            <div className="text-gray-500 mt-1">
                              {formatDateTimeIST(c.createdAt, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Extend Due Date */}
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <CalendarPlus className="h-4 w-4" />
                  Extend Due Date (optional)
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Current:{' '}
                    {replyTask.dueDate
                      ? formatDateTimeIST(replyTask.dueDate, {
                          dateStyle: 'medium',
                        })
                      : 'Not set'}
                  </span>
                  <Input
                    type="date"
                    value={extendDueDate}
                    onChange={e => setExtendDueDate(e.target.value)}
                    min={
                      replyTask.dueDate
                        ? new Date(
                            new Date(replyTask.dueDate).getTime() +
                              24 * 60 * 60 * 1000
                          )
                            .toISOString()
                            .split('T')[0]
                        : new Date().toISOString().split('T')[0]
                    }
                    className="w-40"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Your comment (required)
                </label>
                <Textarea
                  placeholder="Write your reply or feedback for the assignee..."
                  value={replyComment}
                  onChange={e => setReplyComment(e.target.value)}
                  className="mt-1 min-h-[100px]"
                  rows={4}
                />
              </div>
            </div>
          ) : replyTaskId && !replyTaskLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              Task not found.
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReplyTaskId(null)}
              disabled={reopenMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReplySubmit}
              disabled={
                !replyComment.trim() ||
                reopenMutation.isPending ||
                replyTaskLoading
              }
            >
              {reopenMutation.isPending
                ? 'Submitting...'
                : 'Submit & Reopen Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
